import type { Sport, SportType } from '@/types';

export interface SportOption {
  value: SportType;
  label: string;
  icon: string;
  color: string;
}

const primarySportOrder: SportType[] = [
  'football',
  'basketball',
  'volleyball',
  'hockey',
  'tennis',
  'padel',
  'handball',
];

const fallbackSportOrder: SportType[] = ['swimming', 'poker', 'other'];

const sportOrder: SportType[] = [...primarySportOrder, ...fallbackSportOrder];

/** ponytail: убрать из списков, когда раскладки/пресеты будут готовы */
const hiddenSportCodes = new Set<SportType>(['tennis', 'padel', 'poker', 'swimming']);

export const isListedSport = (code: string) => !hiddenSportCodes.has(code as SportType);

const sportOrderMap = new Map<SportType, number>(sportOrder.map((code, idx) => [code, idx]));

export const SPORT_OPTIONS: SportOption[] = [
  { value: 'football', label: 'Футбол', icon: '⚽', color: '#4caf50' },
  { value: 'basketball', label: 'Баскетбол', icon: '🏀', color: '#ff9800' },
  { value: 'volleyball', label: 'Волейбол', icon: '🏐', color: '#9c27b0' },
  { value: 'hockey', label: 'Хоккей', icon: '🏒', color: '#2196f3' },
  { value: 'tennis', label: 'Теннис', icon: '🎾', color: '#e91e63' },
  { value: 'padel', label: 'Падел', icon: '🎾', color: '#8bc34a' },
  { value: 'handball', label: 'Гандбол', icon: '🤾', color: '#00bcd4' },
  { value: 'swimming', label: 'Плавание', icon: '🏊', color: '#03a9f4' },
  { value: 'poker', label: 'Покер', icon: '🃏', color: '#ff5722' },
  { value: 'other', label: 'Другое', icon: '🏆', color: '#757575' },
];

const sportOptionMap = new Map<SportType, SportOption>(SPORT_OPTIONS.map((option) => [option.value, option]));

export const SPORT_FILTER_ORDER: SportType[] = sportOrder.filter(isListedSport);

export const LISTED_SPORT_OPTIONS: SportOption[] = SPORT_OPTIONS.filter((option) => isListedSport(option.value));

export const getSportOption = (sportType: SportType): SportOption | undefined => sportOptionMap.get(sportType);

export const sortSportTypes = <T extends SportType>(sportTypes: T[]): T[] => {
  return [...sportTypes].sort((a, b) => {
    const aOrder = sportOrderMap.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = sportOrderMap.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a).localeCompare(String(b), 'ru');
  });
};

export const sortSports = <T extends Pick<Sport, 'code'>>(sports: T[]): T[] => {
  return [...sports].filter((s) => isListedSport(s.code)).sort((a, b) => {
    const aOrder = sportOrderMap.get(a.code) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = sportOrderMap.get(b.code) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.code).localeCompare(String(b.code), 'ru');
  });
};
